/**
 * Page de listing des exécutions Terraform
 *
 * Fonctionnalités:
 * - Liste paginée des exécutions
 * - Filtres (statut, action, environnement, architecture)
 * - Actions rapides (cancel, retry, view logs)
 * - Refresh automatique
 * - Navigation vers détails
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../Dashboard/DashboardLayout';
import CapacityBar from './CapacityBar';
import {
  listExecutions,
  getExecutionStats,
  cancelExecution,
  retryExecution,
  approveExecution,
  rejectExecution,
  type Execution,
  type ExecutionStats
} from '../../api/executions';
import { listEnvironments, type Environment } from '../../api/environments';
import { getAllArchitectures, type Architecture } from '../../api/architectures';
import { useOrganizationStatus } from '../../hooks/useOrganizationStatus';
import './ExecutionsPage.css';

const BLOCKED_STATUSES = ['TRIAL_EXPIRED', 'SUSPENDED', 'CANCELLED'];

const ExecutionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { organization } = useOrganizationStatus();
  const isBlocked = BLOCKED_STATUSES.includes(organization?.status?.toUpperCase().replace(/-/g, '_') || '');
  const [searchParams, setSearchParams] = useSearchParams();

  const [executions, setExecutions] = useState<Execution[]>([]);
  const [stats, setStats] = useState<ExecutionStats | null>(null);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [architectures, setArchitectures] = useState<Architecture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // Pagination
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);

  // Filtres
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [actionFilter, setActionFilter] = useState<string>(searchParams.get('action') || '');
  const [environmentFilter, setEnvironmentFilter] = useState<string>(searchParams.get('environment') || '');
  const [architectureFilter, setArchitectureFilter] = useState<string>(searchParams.get('architecture') || '');
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');

  // Filtre date — popover style ADO
  const [dateMode, setDateMode] = useState<'24h' | '7d' | '30d' | 'custom' | ''>('');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [dateOpen, setDateOpen] = useState(false);
  const [pendingDateMode, setPendingDateMode] = useState<'24h' | '7d' | '30d' | 'custom' | ''>('');
  const [pendingCustomFrom, setPendingCustomFrom] = useState<string>('');
  const [pendingCustomTo, setPendingCustomTo] = useState<string>('');
  const datePopoverRef = useRef<HTMLDivElement>(null);

  // Fermer le popover si clic extérieur
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (datePopoverRef.current && !datePopoverRef.current.contains(e.target as Node)) {
        setDateOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openDatePopover = () => {
    setPendingDateMode(dateMode);
    setPendingCustomFrom(customFrom);
    setPendingCustomTo(customTo);
    setDateOpen(true);
  };

  const applyDateFilter = () => {
    setDateMode(pendingDateMode);
    setCustomFrom(pendingCustomFrom);
    setCustomTo(pendingCustomTo);
    setOffset(0);
    setDateOpen(false);
  };

  const getDateRange = (): { date_from?: string; date_to?: string } => {
    const now = new Date();
    if (dateMode === '24h') return { date_from: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(), date_to: now.toISOString() };
    if (dateMode === '7d')  return { date_from: new Date(now.getTime() - 7  * 24 * 60 * 60 * 1000).toISOString(), date_to: now.toISOString() };
    if (dateMode === '30d') return { date_from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(), date_to: now.toISOString() };
    if (dateMode === 'custom' && customFrom) return { date_from: new Date(customFrom).toISOString(), date_to: customTo ? new Date(customTo).toISOString() : now.toISOString() };
    return {};
  };

  const getDateLabel = (): string => {
    if (dateMode === '24h')    return 'Last 24 hours';
    if (dateMode === '7d')     return 'Last 7 days';
    if (dateMode === '30d')    return 'Last 30 days';
    if (dateMode === 'custom') return customFrom ? `${customFrom.slice(0, 10)} → ${customTo.slice(0, 10) || '...'}` : 'Custom';
    return 'Toutes les dates';
  };

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);

  /**
   * Charger les exécutions
   */
  const loadExecutions = async () => {
    try {
      setError(null);

      const params: any = {
        limit,
        offset
      };

      if (statusFilter) params.status = statusFilter;
      if (actionFilter) params.action = actionFilter;
      if (environmentFilter) params.environmentId = environmentFilter;
      if (architectureFilter) params.architectureId = architectureFilter;
      if (searchQuery) params.search = searchQuery;
      const { date_from, date_to } = getDateRange();
      if (date_from) params.date_from = date_from;
      if (date_to) params.date_to = date_to;

      const [executionsData, statsData, envsData, archData] = await Promise.all([
        listExecutions(params),
        getExecutionStats({ period: '30d' }),
        listEnvironments(),
        architectures.length === 0 ? getAllArchitectures() : Promise.resolve(architectures)
      ]);

      setExecutions(executionsData.executions);
      setTotal(executionsData.total);
      setStats(statsData);
      setEnvironments(envsData);
      if (archData.length > 0) setArchitectures(archData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load executions');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Effect: Charger les données au montage et sur changement de filtres
   */
  useEffect(() => {
    loadExecutions();
  }, [offset, limit, statusFilter, actionFilter, environmentFilter, architectureFilter, searchQuery, dateMode, customFrom, customTo]);

  /**
   * Effect: Auto-refresh toutes les 5 secondes si activé
   */
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        loadExecutions();
      }, 5000);

      setRefreshInterval(interval);

      return () => clearInterval(interval);
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
  }, [autoRefresh, offset, limit, statusFilter, actionFilter, environmentFilter, architectureFilter, searchQuery, dateMode, customFrom, customTo]);

  /**
   * Appliquer les filtres
   */
  const applyFilters = () => {
    setOffset(0); // Reset pagination
    const params: any = {};
    if (statusFilter) params.status = statusFilter;
    if (actionFilter) params.action = actionFilter;
    if (environmentFilter) params.environment = environmentFilter;
    if (searchQuery) params.search = searchQuery;
    setSearchParams(params);
  };

  /**
   * Reset les filtres
   */
  const resetFilters = () => {
    setStatusFilter('');
    setActionFilter('');
    setEnvironmentFilter('');
    setArchitectureFilter('');
    setSearchQuery('');
    setDateMode('');
    setCustomFrom('');
    setCustomTo('');
    setOffset(0);
    setSearchParams({});
  };

  /**
   * Annuler une exécution
   */
  const handleCancel = async (runId: string) => {
    if (!window.confirm('Are you sure you want to cancel this execution?')) return;

    try {
      await cancelExecution(runId, 'Cancelled by user');
      await loadExecutions();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to cancel execution');
    }
  };

  /**
   * Approuver une exécution depuis la liste
   */
  const handleApproveInline = async (runId: string) => {
    try {
      await approveExecution(runId);
      await loadExecutions();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve execution');
    }
  };

  /**
   * Rejeter une exécution depuis la liste
   */
  const handleRejectInline = async (runId: string) => {
    const reason = window.prompt('Raison du rejet :');
    if (reason === null) return; // cancelled
    try {
      await rejectExecution(runId, reason || 'Rejeté depuis la liste');
      await loadExecutions();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reject execution');
    }
  };

  /**
   * Retry une exécution
   */
  const handleRetry = async (runId: string) => {
    try {
      const newExecution = await retryExecution(runId);
      navigate(`/executions/${newExecution.run_id}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to retry execution');
    }
  };

  /**
   * Naviguer vers les détails
   */
  const viewDetails = (runId: string) => {
    navigate(`/executions/${runId}`);
  };

  /**
   * Créer une nouvelle exécution
   */
  const createNew = () => {
    navigate('/executions/new');
  };

  /**
   * Obtenir la classe CSS pour le badge de statut
   */
  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      pending: 'status-pending',
      queued: 'status-queued',
      running: 'status-running',
      success: 'status-success',
      succeeded: 'status-success',
      failed: 'status-failed',
      cancelled: 'status-cancelled',
      pending_approval: 'status-approval',
      approved: 'status-approved'
    };
    return statusMap[status] || 'status-default';
  };

  /**
   * Obtenir l'icône pour le statut
   */
  const getStatusIcon = (status: string): string => {
    const icons: Record<string, string> = {
      pending: '⏳',
      queued: '📋',
      running: '▶️',
      success: '✅',
      succeeded: '✅',
      failed: '❌',
      cancelled: '⊘',
      pending_approval: '⏸️',
      approved: '✓'
    };
    return icons[status] || '•';
  };

  /**
   * Obtenir l'icône pour l'action
   */
  const getActionIcon = (action: string): string => {
    const icons: Record<string, string> = {
      plan: '📋',
      apply: '🚀',
      destroy: '🗑️'
    };
    return icons[action] || '•';
  };

  /**
   * Formater la durée
   */
  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  /**
   * Formater la date
   */
  const formatDate = (date: string): string => {
    return new Date(date).toLocaleString();
  };

  /**
   * Calculer la durée relative
   */
  const getRelativeTime = (date: string): string => {
    const now = new Date().getTime();
    const then = new Date(date).getTime();
    const diff = now - then;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading && executions.length === 0) {
    return (
      <DashboardLayout>
        <div className="executions-page">
          <div className="loading">Loading executions...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8 text-gray-900">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🚀 Terraform Executions</h1>
          <p className="text-gray-600">Manage and monitor your infrastructure deployments</p>
        </div>
        <div className="flex gap-3">
          <button
            className={`flex items-center gap-2 px-6 py-3 rounded-lg transition shadow-md font-medium ${isBlocked ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            onClick={isBlocked ? undefined : createNew}
            disabled={isBlocked}
            title={isBlocked ? 'Votre période d\'essai est expirée. Choisissez un plan pour continuer.' : undefined}
          >
            ➕ New Execution
          </button>
        </div>
      </div>

      {/* Capacity Bar */}
      <div className="mb-6">
        <CapacityBar />
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center shadow-sm">
            <div className="text-xs text-gray-600 uppercase tracking-wide mb-2">Total</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total || 0}</div>
          </div>
          <div className="bg-white rounded-lg border-l-4 border-green-500 border-t border-r border-b border-gray-200 p-4 text-center shadow-sm">
            <div className="text-xs text-gray-600 uppercase tracking-wide mb-2">Succeeded</div>
            <div className="text-3xl font-bold text-gray-900">{stats.by_status?.succeeded || 0}</div>
          </div>
          <div className="bg-white rounded-lg border-l-4 border-blue-500 border-t border-r border-b border-gray-200 p-4 text-center shadow-sm">
            <div className="text-xs text-gray-600 uppercase tracking-wide mb-2">Running</div>
            <div className="text-3xl font-bold text-gray-900">{stats.by_status?.running || 0}</div>
          </div>
          <div className="bg-white rounded-lg border-l-4 border-red-500 border-t border-r border-b border-gray-200 p-4 text-center shadow-sm">
            <div className="text-xs text-gray-600 uppercase tracking-wide mb-2">Failed</div>
            <div className="text-3xl font-bold text-gray-900">{stats.by_status?.failed || 0}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center shadow-sm">
            <div className="text-xs text-gray-600 uppercase tracking-wide mb-2">Success Rate</div>
            <div className="text-3xl font-bold text-gray-900">{(stats.success_rate || 0).toFixed(1)}%</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center shadow-sm">
            <div className="text-xs text-gray-600 uppercase tracking-wide mb-2">Avg Duration</div>
            <div className="text-3xl font-bold text-gray-900">{formatDuration(stats.average_duration_seconds || 0)}</div>
          </div>
        </div>
      )}

      {/* Pending Approvals Banner */}
      {stats && (stats.by_status?.pending_approval || 0) > 0 && (
        <div
          className="flex items-center justify-between bg-amber-50 border border-amber-400 rounded-lg p-4 mb-4 cursor-pointer hover:bg-amber-100 transition shadow-sm"
          onClick={() => { setStatusFilter('pending_approval'); setOffset(0); }}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">⏸️</span>
            <div>
              <div className="font-semibold text-amber-900">
                {stats.by_status.pending_approval} exécution{stats.by_status.pending_approval > 1 ? 's' : ''} en attente d'approbation
              </div>
              <div className="text-sm text-amber-700">Cliquez pour voir les exécutions qui nécessitent votre approbation</div>
            </div>
          </div>
          <span className="text-amber-600 font-medium text-sm">Voir →</span>
        </div>
      )}

      {/* Quick Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${statusFilter === '' && !actionFilter ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
          onClick={() => { setStatusFilter(''); setActionFilter(''); setOffset(0); }}
        >
          Toutes
        </button>
        <button
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${statusFilter === 'pending_approval' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'}`}
          onClick={() => { setStatusFilter('pending_approval'); setOffset(0); }}
        >
          ⏸️ En attente d'approbation {stats && (stats.by_status?.pending_approval || 0) > 0 && <span className="ml-1 bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-full">{stats.by_status.pending_approval}</span>}
        </button>
        <button
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${statusFilter === 'running' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}
          onClick={() => { setStatusFilter('running'); setOffset(0); }}
        >
          ▶️ En cours {stats && (stats.by_status?.running || 0) > 0 && <span className="ml-1 bg-blue-200 text-blue-900 px-1.5 py-0.5 rounded-full">{stats.by_status.running}</span>}
        </button>
        <button
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${statusFilter === 'failed' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-700 border-red-300 hover:bg-red-50'}`}
          onClick={() => { setStatusFilter('failed'); setOffset(0); }}
        >
          ❌ Échoués {stats && (stats.by_status?.failed || 0) > 0 && <span className="ml-1 bg-red-200 text-red-900 px-1.5 py-0.5 rounded-full">{stats.by_status.failed}</span>}
        </button>
        <button
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${actionFilter === 'apply' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-green-700 border-green-300 hover:bg-green-50'}`}
          onClick={() => { setActionFilter('apply'); setStatusFilter(''); setOffset(0); }}
        >
          🚀 Apply
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6 shadow-sm">
        <div className="flex gap-3 flex-wrap items-end justify-between">
          <div className="flex gap-3 flex-wrap items-end flex-1">

            {/* Search Run ID */}
            <div className="flex flex-col gap-1.5 min-w-[180px]">
              <label className="text-xs text-gray-500 font-medium uppercase tracking-wide">Run ID</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filtrer par ID..."
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Date filter — popover style ADO */}
            <div className="flex flex-col gap-1.5 relative" ref={datePopoverRef}>
              <label className="text-xs text-gray-500 font-medium uppercase tracking-wide">Période</label>
              <button
                onClick={openDatePopover}
                className={`px-3 py-2 border rounded-md text-sm text-left flex items-center gap-2 min-w-[170px] transition ${
                  dateMode ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="flex-1">{getDateLabel()}</span>
                <span className="text-gray-400">▾</span>
              </button>

              {dateOpen && (
                <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-4 w-72">
                  <div className="font-semibold text-gray-800 mb-3 text-sm">Plage d'heure de fin</div>
                  <div className="flex flex-col gap-2 mb-4">
                    {([
                      { value: '24h', label: 'Last 24 hours' },
                      { value: '7d',  label: 'Last 7 days' },
                      { value: '30d', label: 'Last 30 days' },
                      { value: 'custom', label: 'Custom' },
                    ] as const).map(({ value, label }) => (
                      <label key={value} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 hover:text-gray-900">
                        <input
                          type="radio"
                          name="dateMode"
                          checked={pendingDateMode === value}
                          onChange={() => setPendingDateMode(value)}
                          className="accent-blue-600"
                        />
                        {label}
                      </label>
                    ))}
                  </div>

                  {pendingDateMode === 'custom' && (
                    <div className="flex flex-col gap-2 mb-4 border-t pt-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-gray-500 font-medium">De</label>
                        <input
                          type="datetime-local"
                          value={pendingCustomFrom}
                          onChange={(e) => setPendingCustomFrom(e.target.value)}
                          className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-gray-500 font-medium">À</label>
                        <input
                          type="datetime-local"
                          value={pendingCustomTo}
                          onChange={(e) => setPendingCustomTo(e.target.value)}
                          className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2 border-t">
                    <button onClick={applyDateFilter} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition">
                      OK
                    </button>
                    <button onClick={() => { setPendingDateMode(''); setDateOpen(false); setDateMode(''); setOffset(0); }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50 transition">
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="flex flex-col gap-1.5 min-w-[150px]">
              <label className="text-xs text-gray-500 font-medium uppercase tracking-wide">État</label>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Tous</option>
                <option value="running">En cours</option>
                <option value="succeeded">Succès</option>
                <option value="failed">Échoué</option>
                <option value="cancelled">Annulé</option>
                <option value="pending_approval">En attente</option>
              </select>
            </div>

            {/* Action */}
            <div className="flex flex-col gap-1.5 min-w-[130px]">
              <label className="text-xs text-gray-500 font-medium uppercase tracking-wide">Action</label>
              <select value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setOffset(0); }} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Toutes</option>
                <option value="plan">Plan</option>
                <option value="apply">Apply</option>
                <option value="destroy">Destroy</option>
              </select>
            </div>

            {/* Architecture */}
            <div className="flex flex-col gap-1.5 min-w-[180px]">
              <label className="text-xs text-gray-500 font-medium uppercase tracking-wide">Architecture</label>
              <select value={architectureFilter} onChange={(e) => { setArchitectureFilter(e.target.value); setOffset(0); }} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Toutes</option>
                {architectures.map((arch) => (
                  <option key={arch.id} value={arch.id}>{arch.name}</option>
                ))}
              </select>
            </div>

            {/* Environment */}
            <div className="flex flex-col gap-1.5 min-w-[160px]">
              <label className="text-xs text-gray-500 font-medium uppercase tracking-wide">Environnement</label>
              <select value={environmentFilter} onChange={(e) => { setEnvironmentFilter(e.target.value); setOffset(0); }} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Tous</option>
                {environments.map((env) => (
                  <option key={env.id} value={env.id}>{env.displayName || env.name}</option>
                ))}
              </select>
            </div>

            {/* Reset */}
            {(statusFilter || actionFilter || environmentFilter || architectureFilter || searchQuery || dateMode) && (
              <div className="flex items-end">
                <button onClick={resetFilters} className="px-3 py-2 text-sm text-gray-500 hover:text-red-600 border border-gray-300 hover:border-red-300 rounded-md transition">
                  ✕ Reset
                </button>
              </div>
            )}
          </div>

          {/* Auto-refresh */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="cursor-pointer" />
              Auto-refresh (5s)
            </label>
            <button className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 transition" onClick={loadExecutions} title="Rafraîchir">
              🔄
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-400 rounded-md p-3 mb-4 text-yellow-800 flex items-center gap-2">
          ⚠️ {error}
        </div>
      )}

      {/* Executions Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4 shadow-sm">
        <table className="w-full border-collapse">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Run ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Architecture</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Environment</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Version</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Initiated By</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Started</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Duration</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Engine</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {executions.length === 0 ? (
              <tr>
                <td colSpan={11} className="text-center py-16 text-gray-500 text-sm">
                  No executions found. Create your first execution to get started!
                </td>
              </tr>
            ) : (
              executions.filter(e => e && e.run_id).map((execution) => (
                <tr key={execution.run_id} className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-3.5 text-sm text-gray-900 align-middle">
                    <code className="font-mono text-blue-600 cursor-pointer hover:underline text-xs" onClick={() => viewDetails(execution.run_id)}>
                      {execution.run_id}
                    </code>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-900 align-middle">
                    <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getActionIcon(execution.action || 'plan')} {execution.action || 'plan'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-900 align-middle">
                    <span className={`status-badge ${getStatusClass(execution.status || 'pending')}`}>
                      {getStatusIcon(execution.status || 'pending')} {(execution.status || 'pending').replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-900 align-middle">{execution.architecture_name || execution.architecture_id?.substring(0, 8) || '-'}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-900 align-middle">
                    <span className={`env-badge env-${execution.environment_type || 'dev'}`}>
                      {execution.environment_name || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-900 align-middle">v{execution.version_number || '?'}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-900 align-middle">{execution.initiated_by_name || execution.initiated_by?.substring(0, 8) || '-'}</td>
                  <td title={formatDate(execution.started_at || execution.created_at)}>
                    {getRelativeTime(execution.started_at || execution.created_at)}
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-900 align-middle">{formatDuration(execution.duration_seconds)}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-900 align-middle">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                      execution.engine_type === 'mock' ? 'bg-purple-100 text-purple-800' :
                      execution.engine_type === 'azure-devops' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {execution.engine_type === 'mock' && '🧪 Mock'}
                      {execution.engine_type === 'azure-devops' && '☁️ ADO'}
                      {execution.engine_type === 'kubernetes-job' && '⚙️ K8s'}
                      {execution.engine_type && !['mock', 'azure-devops', 'kubernetes-job'].includes(execution.engine_type) && execution.engine_type}
                      {!execution.engine_type && '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-900 align-middle">
                    <div className="flex gap-1.5">
                      <button
                        className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 transition text-base"
                        onClick={() => viewDetails(execution.run_id)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      {execution.action === 'plan' && (execution.status === 'success' || execution.status === 'succeeded') && (
                        <button
                          className="p-1.5 border border-green-300 rounded hover:bg-green-50 hover:border-green-500 transition text-base"
                          onClick={() => navigate(`/executions/new?action=apply&plan_run_id=${execution.run_id}&architecture_id=${execution.architecture_id}&environment_id=${execution.environment_id}&version_id=${execution.architecture_version_id}`)}
                          title="Create Apply from this Plan"
                        >
                          🚀
                        </button>
                      )}
                      {['pending', 'queued', 'running'].includes(execution.status) && (
                        <button
                          className="p-1.5 border border-gray-300 rounded hover:bg-red-50 hover:border-red-400 transition text-base"
                          onClick={() => handleCancel(execution.run_id)}
                          title="Cancel"
                        >
                          ✖️
                        </button>
                      )}
                      {execution.status === 'failed' && (
                        <button
                          className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 transition text-base"
                          onClick={() => handleRetry(execution.run_id)}
                          title="Retry"
                        >
                          🔄
                        </button>
                      )}
                      {execution.status === 'pending_approval' && (
                        <>
                          <button
                            className="p-1.5 border border-green-400 rounded hover:bg-green-50 transition text-base"
                            onClick={(e) => { e.stopPropagation(); handleApproveInline(execution.run_id); }}
                            title="Approuver"
                          >
                            ✅
                          </button>
                          <button
                            className="p-1.5 border border-red-400 rounded hover:bg-red-50 transition text-base"
                            onClick={(e) => { e.stopPropagation(); handleRejectInline(execution.run_id); }}
                            title="Rejeter"
                          >
                            ❌
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (() => {
        const totalPages = Math.max(1, Math.ceil(total / limit));
        const currentPage = Math.floor(offset / limit) + 1;
        const goToPage = (p: number) => setOffset((p - 1) * limit);
        return (
          <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
            {/* Rows per page */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Lignes par page :</span>
              {[20, 50, 100, 200].map(n => (
                <button
                  key={n}
                  onClick={() => { setLimit(n); setOffset(0); }}
                  className={`px-3 py-1 rounded-md border text-sm transition ${
                    limit === n
                      ? 'bg-gray-800 text-white border-gray-800 font-semibold'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>

            {/* Page info + nav */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>{offset + 1}–{Math.min(offset + limit, total)} sur {total}</span>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce<(number | '…')[]>((acc, p, i, arr) => {
                  if (i > 0 && (p as number) - (arr[i - 1] as number) > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} className="px-1 text-gray-400">…</span>
                    : <button
                        key={p}
                        onClick={() => goToPage(p as number)}
                        className={`px-3 py-1 rounded-md border text-sm transition min-w-[32px] ${
                          currentPage === p
                            ? 'bg-gray-800 text-white border-gray-800 font-semibold'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                )}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition"
              >
                ›
              </button>
            </div>
          </div>
        );
      })()}
    </div>
    </DashboardLayout>
  );
};

export default ExecutionsPage;
